import React, { useState, useEffect } from 'react';
import { getDeals, createDeal, deleteDeal, getLegalEntities } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { formatDate, getStatusColor } from '../lib/utils';
import { Plus, Briefcase, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const STAGES = ['Sourcing', 'DD', 'IC', 'Negotiation', 'Closing', 'Closed', 'Pass'];
const STATUSES = ['Active', 'On Hold', 'Closed', 'Passed'];

const DealsPage = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    sector: '',
    stage: 'Sourcing',
    geography: '',
    ticket_size_range: '',
    status: 'Active'
  });

  const fetchData = async () => {
    try {
      const response = await getDeals({});
      setDeals(response.data);
    } catch (error) {
      toast.error('Failed to load deals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createDeal(formData);
      toast.success('Deal created');
      setDialogOpen(false);
      setFormData({ name: '', sector: '', stage: 'Sourcing', geography: '', ticket_size_range: '', status: 'Active' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create deal');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this deal?')) return;
    try {
      await deleteDeal(id);
      toast.success('Deal deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-zinc-500 font-inter">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="deals-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope text-xl font-bold text-zinc-900">Deals</h1>
          <p className="text-sm text-zinc-500 font-inter">Investment opportunities pipeline</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm" data-testid="create-deal-btn">
              <Plus className="w-4 h-4 mr-1" /> New Deal
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader>
              <DialogTitle className="font-manrope">Create Deal</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label className="font-inter text-sm">Deal Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="rounded-sm"
                  placeholder="Company Name Series A"
                  required
                  data-testid="deal-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-inter text-sm">Sector</Label>
                  <Input
                    value={formData.sector}
                    onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
                    className="rounded-sm"
                    placeholder="Technology, Healthcare..."
                    required
                  />
                </div>
                <div>
                  <Label className="font-inter text-sm">Geography</Label>
                  <Input
                    value={formData.geography}
                    onChange={(e) => setFormData({ ...formData, geography: e.target.value })}
                    className="rounded-sm"
                    placeholder="India, US, Singapore..."
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-inter text-sm">Stage</Label>
                  <Select value={formData.stage} onValueChange={(v) => setFormData({ ...formData, stage: v })}>
                    <SelectTrigger className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-inter text-sm">Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                    <SelectTrigger className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="font-inter text-sm">Ticket Size Range</Label>
                <Input
                  value={formData.ticket_size_range}
                  onChange={(e) => setFormData({ ...formData, ticket_size_range: e.target.value })}
                  className="rounded-sm"
                  placeholder="$1M-5M"
                  required
                />
              </div>
              <Button type="submit" className="w-full rounded-sm" data-testid="submit-deal-btn">
                Create Deal
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Deals List */}
      {deals.length === 0 ? (
        <Card className="border border-zinc-200 shadow-none rounded-sm">
          <CardContent className="p-8 text-center">
            <Briefcase className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
            <p className="text-zinc-500 font-inter">No deals yet. Create one to start tracking.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {deals.map(deal => (
            <Card key={deal.id} className="border border-zinc-200 shadow-none rounded-sm hover:border-zinc-300 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-medium font-inter text-zinc-900">{deal.name}</h3>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge className={getStatusColor(deal.stage)}>{deal.stage}</Badge>
                      <Badge variant="outline" className="text-xs">{deal.status}</Badge>
                    </div>
                    <div className="mt-3 text-xs text-zinc-500 font-mono space-y-1">
                      <p>Sector: {deal.sector}</p>
                      <p>Geography: {deal.geography}</p>
                      <p>Ticket: {deal.ticket_size_range}</p>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="text-zinc-400 hover:text-red-500"
                    onClick={() => handleDelete(deal.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default DealsPage;
